import { Component, Input, OnDestroy, OnInit } from '@angular/core'
import { UntypedFormControl } from '@angular/forms'
import { MatDialog } from '@angular/material/dialog'
import { Router } from '@angular/router'
import {
  ApiService,
  Appointment,
  AppointmentDataService,
  ClinicService,
  ConfirmService,
  CustomQuestionSchema,
  FileService,
  getIntakeSections,
  getQuestionsBySection,
  Intake,
  IntakeModel,
  IntakeSection,
  IntakeService,
  IQuestion,
  IQuestionMap,
  ISection,
  ISectionPDFDocumentValue,
  MessageTemplate,
  PatientDataService,
  PatientIntakeFormData,
  PatientPalRestError,
  PatientUploadedFile,
  SendPatientMessageMethod,
} from '@mmx/shared'
import { find, get, includes, orderBy } from 'lodash'
import { Observable, of, Subject } from 'rxjs'
import {
  catchError,
  combineLatestWith,
  filter,
  finalize,
  first,
  map,
  shareReplay,
  startWith,
  switchMap,
  take,
  takeUntil,
  tap,
} from 'rxjs/operators'

import { PatientFileViewComponent } from '../patient-file-view'
import { ProgressService } from '../progress'
import {
  SendContactEditDialogResult,
  SendContactEditDialogService,
} from '../send-contact-edit-dialog'

enum PROGRESS_KEY {
  CREATE_INTAKE = 'create intake',
  DOWNLOAD = 'download',
  FILES = 'files',
  INTAKE = 'intake',
  INTAKES = 'intakes',
  LINK_INTAKE = 'link intake',
  QUESTIONS = 'questions',
  SECTIONS = 'sections',
  SEND_MESSAGE = 'send message',
}

@Component({
  selector: 'mmx-intake-card',
  templateUrl: './intake-card.component.html',
  standalone: false,
})
export class IntakeCardComponent implements OnInit, OnDestroy {
  @Input() embedded = false
  intakeData = new PatientIntakeFormData()
  intakeControl = new UntypedFormControl()
  filesLoadError = false

  progress$ = this.progressService.progress$.pipe(
    combineLatestWith(this.data.progress$),
    map(
      ([localLoading, dataServiceLoading]) =>
        localLoading || dataServiceLoading,
    ),
  )

  private onDestroy$: Subject<void> = new Subject<void>()

  imageQuestions$: Observable<CustomQuestionSchema[]> = this.data.intake$.pipe(
    map((intake) =>
      (intake?.customQuestions || []).filter(
        (question) => question.value && question.isImage,
      ),
    ),
  )

  canSendIntakeLinkToPatient$: Observable<boolean> =
    this.data.appointment$.pipe(
      filter(Boolean),
      map((appointment) => {
        return !includes(
          [
            Appointment.INTAKE_STATUS.SUBMITTED,
            Appointment.INTAKE_STATUS.CONFIRMED,
            Appointment.INTAKE_STATUS.TECH_CONFIRMED,
            Appointment.INTAKE_STATUS.TECH_REVIEW,
            Appointment.INTAKE_STATUS.SYNCING,
            Appointment.INTAKE_STATUS.SYNCED,
            Appointment.INTAKE_STATUS.NOT_REQUIRED,
          ],
          appointment.intakeStatus,
        )
      }),
    )

  private intakeAndPatient$ = this.data.intake$.pipe(
    takeUntil(this.onDestroy$),
    combineLatestWith(this.data.patient$.pipe(takeUntil(this.onDestroy$))),
  )

  hasUpdatedPatientName$: Observable<boolean> = this.intakeAndPatient$.pipe(
    map(([intake, patient]) => {
      return (
        intake != null &&
        (intake.givenName?.toUpperCase() !==
          patient.name.given?.toUpperCase() ||
          intake.familyName?.toUpperCase() !==
            patient.name.family?.toUpperCase())
      )
    }),
    shareReplay(1),
  )

  hasUpdatedPatientDOB$: Observable<boolean> = this.intakeAndPatient$.pipe(
    map(([intake, patient]) => {
      return (
        intake != null &&
        intake.dob != null &&
        !intake.dob.isSame(patient.dob, 'day')
      )
    }),
    shareReplay(1),
  )

  hasUpdatedPatientSex$: Observable<boolean> = this.intakeAndPatient$.pipe(
    map(
      ([intake, patient]) =>
        intake != null &&
        intake.sex != null &&
        patient.sex != null &&
        intake.sex !== patient.sex,
    ),
    shareReplay(1),
  )

  warning$: Observable<boolean> = this.hasUpdatedPatientName$.pipe(
    combineLatestWith(this.hasUpdatedPatientDOB$, this.hasUpdatedPatientSex$),
    map(([name, dob, sex]) => name || dob || sex),
    shareReplay(1),
  )

  private refreshFiles$ = new Subject<void>()

  customDocumentTypes$ = this.clinicService.clinic$.pipe(
    takeUntil(this.onDestroy$),
    map((clinic) => clinic.settings.customDocumentTypes ?? []),
  )

  pdfDocuments$: Observable<ISectionPDFDocumentValue[]> =
    this.data.intakeId$.pipe(
      takeUntil(this.onDestroy$),
      switchMap((intakeId) =>
        this.restService
          .getIntakePDFDocuments(intakeId)
          .pipe(takeUntil(this.onDestroy$)),
      ),
      // finalize(() => this.progressBar.mode = 'determinate'),
      map((resp) => resp.data),
    )

  customSections$: Observable<ISection[]> = this.data.intake$.pipe(
    takeUntil(this.onDestroy$),
    tap(() => this.progressService.start(PROGRESS_KEY.SECTIONS)),
    switchMap((intake) =>
      this.intakeService
        .getIntakeSections({
          appointment: intake.appointmentId,
          intake: intake.id,
          includeHidden: 'Y',
        })
        .pipe(
          takeUntil(this.onDestroy$),
          finalize(() => this.progressService.stop(PROGRESS_KEY.SECTIONS)),
        ),
    ),
    map((resp) => resp.data),
    shareReplay(1),
  )

  questionsByLeg$: Observable<IQuestionMap> = this.data.appointmentId$.pipe(
    takeUntil(this.onDestroy$),
    tap(() => this.progressService.start(PROGRESS_KEY.QUESTIONS)),
    switchMap((appointmentId) =>
      this.intakeService.getQuestions({ appointment: appointmentId }).pipe(
        takeUntil(this.onDestroy$),
        finalize(() => this.progressService.stop(PROGRESS_KEY.QUESTIONS)),
      ),
    ),
    map((resp) => resp.data),
    shareReplay(1),
  )

  private questions: IQuestion[] = []

  questions$: Observable<IQuestion[]> = this.questionsByLeg$.pipe(
    map((questionsByLeg) => {
      const questions: IQuestion[] = []
      const recursive = (questions: IQuestion[]) => {
        questions.push(...questions)

        for (const question of questions) {
          if (question.questions) {
            recursive(question.questions)
          }
        }
      }

      for (const section of questionsByLeg) {
        recursive(section.questions)
      }

      return questions
    }),
    tap((questions) => (this.questions = questions)),
    shareReplay(1),
  )

  sections$: Observable<IntakeSection[]> = this.data.intake$.pipe(
    takeUntil(this.onDestroy$),
    filter(Boolean),
    combineLatestWith(
      this.clinicService.clinic$.pipe(takeUntil(this.onDestroy$)),
      this.data.appointment$.pipe(takeUntil(this.onDestroy$)),
      this.customSections$.pipe(takeUntil(this.onDestroy$)),
      this.questionsByLeg$.pipe(takeUntil(this.onDestroy$)),
    ),
    map(([intake, clinic, appointment, customSections, questionsByLeg]) => {
      this.intakeData.fromJson(intake)
      const allSections = getIntakeSections(
        clinic,
        appointment,
        this.intakeData,
        customSections,
        questionsByLeg,
      )

      const sections = allSections.filter((section) => {
        const questions = getQuestionsBySection(
          section.label,
          appointment,
          this.intakeData,
          questionsByLeg,
        )

        section.questions = questions.filter((customQuestion) => {
          const question = find(intake.customQuestions, {
            id: customQuestion.id,
          })
          // Show only favorite questions
          if (question) {
            return (
              customQuestion.favorite ||
              (customQuestion.expectedAnswer != null &&
                question.value !== customQuestion.expectedAnswer)
            )
          }
        })
        return section
      })

      return orderBy(sections, 'order')
    }),
  )

  private uploadedFiles$ = this.data.intakeId$.pipe(
    takeUntil(this.onDestroy$),
    filter(Boolean),
    combineLatestWith(
      this.data.patient$.pipe(takeUntil(this.onDestroy$)),
      this.refreshFiles$.pipe(takeUntil(this.onDestroy$), startWith(null)),
    ),
    tap(() => this.progressService.start(PROGRESS_KEY.FILES)),
    switchMap(([intakeId, patient]) =>
      this.restService.getPatientUploadedFiles(patient.id, intakeId).pipe(
        takeUntil(this.onDestroy$),
        finalize(() => this.progressService.stop(PROGRESS_KEY.FILES)),
      ),
    ),
    map((resp) => resp.data),
    catchError(() => {
      this.filesLoadError = true
      return of([])
    }),
    shareReplay(1),
  )

  uploadedPhotos$ = this.uploadedFiles$.pipe(
    map((files) => files.filter((file) => file.type === 'image')),
    shareReplay(1),
  )

  uploadedDocuments$ = this.uploadedFiles$.pipe(
    map((files) => files.filter((file) => file.type !== 'image')),
    shareReplay(1),
  )

  intakes$ = this.patientDataService.patientId$.pipe(
    filter(Boolean),
    tap(() => this.progressService.start(PROGRESS_KEY.INTAKES)),
    switchMap((patientId) =>
      this.restService.getPatientDocuments(patientId, ['intakes']).pipe(
        takeUntil(this.onDestroy$),
        finalize(() => this.progressService.stop(PROGRESS_KEY.INTAKES)),
      ),
    ),
    shareReplay(1),
  )

  private sendMessage$ = new Subject<{ method: SendPatientMessageMethod; to?: string }>()

  private sendMessageMethod$ = this.sendMessage$
    .pipe(
      takeUntil(this.onDestroy$), // Ensures cleanup on component destruction
      filter(Boolean),
      switchMap(({ method, to }) =>
        this.data.appointment$.pipe(
          first(), // Takes the first emitted appointment to proceed with
          takeUntil(this.onDestroy$),
          switchMap((appointment) => {
            if (
              appointment.intakeStatus === Appointment.INTAKE_STATUS.DELIVERED
            ) {
              // If the message was already sent
              return this.confirmService
                .confirm(
                  'This patient has already received an intake message. Would you like to send it again?',
                )
                .pipe(
                  takeUntil(this.onDestroy$),
                  filter((isConfirmed) => isConfirmed), // Only proceed if the user confirmed to resend
                  map(() => ({ method, to, appointment })),
                )
            } else {
              // If the message has not been sent, directly proceed
              return of({ method, to, appointment })
            }
          }),
        ),
      ),
      tap(() => this.progressService.start(PROGRESS_KEY.SEND_MESSAGE)),
      switchMap(({ method, to, appointment }) =>
        this.restService
          .sendPatientMessage(appointment.patientId, {
            type: MessageTemplate.TYPE.INTAKE_LINK,
            method,
            appointmentId: appointment.id,
            to,
          })
          .pipe(
            finalize(() => {
              // Stop the progress indicator when the message sending process is done
              this.progressService.stop(PROGRESS_KEY.SEND_MESSAGE)
              this.data.appointmentUpdated$.next()
            }),
          ),
      ),
    )
    .subscribe()

  openSendDialog(method: SendPatientMessageMethod): void {
    this.data.patient$.pipe(first()).subscribe((patient) => {
      this.sendContactEditDialogService.open({
        method,
        phone: patient?.phone,
        email: patient?.email,
      })
        .pipe(take(1))
        .subscribe((result: SendContactEditDialogResult | undefined) => {
          if (result !== undefined) {
            this.sendMessage$.next({ method, to: result.to })
          }
        })
    })
  }

  addIntake$ = new Subject<void>()

  private createIntake$ = this.addIntake$.pipe(
    takeUntil(this.onDestroy$),
    first(),
    combineLatestWith(
      this.data.appointmentId$.pipe(takeUntil(this.onDestroy$)),
      this.data.patient$.pipe(takeUntil(this.onDestroy$)),
    ),
    tap(() => this.progressService.start(PROGRESS_KEY.CREATE_INTAKE)),
    switchMap(([, appointmentId, patient]) => {
      const intake = new IntakeModel({
        appointmentId: appointmentId,
        patientId: patient.id,
        givenName: patient.name.given,
        familyName: patient.name.family,
        email: patient.email,
        dob: patient.dob?.format('YYYY-MM-DD'),
        sex: patient.sex,
        phoneNumber: patient.phone,
        address: patient.address,
        stage: Intake.STAGE.PENDING,
      })
      return this.restService.createIntake(intake).pipe(
        takeUntil(this.onDestroy$),
        finalize(() => this.progressService.start(PROGRESS_KEY.CREATE_INTAKE)),
      )
    }),
  )

  constructor(
    public data: AppointmentDataService,
    private dialog: MatDialog,
    private sendContactEditDialogService: SendContactEditDialogService,
    private intakeService: IntakeService,
    private clinicService: ClinicService,
    private fileService: FileService,
    private confirmService: ConfirmService,
    private progressService: ProgressService<PROGRESS_KEY>,
    private patientDataService: PatientDataService,
    private restService: ApiService,
    private router: Router,
  ) {}

  ngOnInit() {
    this.createIntake$.pipe(takeUntil(this.onDestroy$)).subscribe({
      next: (response) => {
        if (response.success) {
          this.data.appointmentUpdated$.next()

          if (this.embedded) {
            return
          }

          this.router.navigate([
            'patient',
            response.data.patientId,
            'appointment',
            response.data.appointmentId,
            'intake',
            response.data.id,
          ])
        }
      },
      error: (err: PatientPalRestError) => {
        const errorMessage =
          get(err, 'error.errors[0].message') ||
          err.error.message ||
          'Unable to add intake. Please try again.'
        alert(errorMessage)
        // progress.stop({
        //   message: errorMessage,
        //   panelClass: 'warn',
        // })
      },
    })
  }

  // ngOnChanges(changes: SimpleChanges) {
  //   if (this.intake != null) {
  //     this.progressBar.mode = 'determinate'
  //     this.loadFiles()
  //   } else if (this.appointment != null && this.intake == null && this.intakeId == null) {
  //     this.loadOldIntakes()
  //   }

  //   if (changes.intake) {
  //     if (this.intake) {
  //       this.getPDFDocuments()
  //       this.intakeService.getIntakeSections({
  //         appointment: this.intake.appointmentId,
  //         intake: this.intake.id,
  //         includeHidden: 'Y',
  //       }).subscribe(response => {
  //         this.customSections = response.data
  //         this.getSections()
  //       })
  //     }
  //   }
  // }

  ngOnDestroy(): void {
    this.onDestroy$.next()
    this.onDestroy$.complete()
  }

  viewFile(file: PatientUploadedFile) {
    const dialogRef = this.dialog.open(PatientFileViewComponent, {
      panelClass: 'dialog-form',
      width: file.extension === '.pdf' ? '2000px' : '500px',
      data: file,
    })

    dialogRef.afterClosed().subscribe((fileDeleted) => {
      if (fileDeleted) {
        this.refreshFiles$.next()
      }
    })
  }

  getQuestionField(id: string, field: string) {
    const question = this.questions.find((q) => q.id === id)
    return question ? question[field] : ''
  }

  onIntakeSelectionSubmit(appointmentId: string): void {
    if (this.intakeControl.value) {
      const intakeId = this.intakeControl.value

      // Subscribe to the intakes$ observable to get the current list of intakes.
      this.intakes$
        .pipe(
          first(), // Takes the first emitted array of intakes and completes
          map((intakes) => intakes.find((intake) => intake.id === intakeId)), // Find the specific intake.
          filter(Boolean),
          tap(() => this.progressService.start(PROGRESS_KEY.LINK_INTAKE)), // Start the progress indicator.
          switchMap((intake) =>
            this.restService.patchAppointment(appointmentId, {
              intakeId,
              intakeStatus: intake.status,
            }),
          ),
          finalize(() => {
            this.progressService.stop(PROGRESS_KEY.LINK_INTAKE) // Stop the progress indicator.
            this.data.appointmentUpdated$.next() // Notify that the appointment has been updated.
          }),
          takeUntil(this.onDestroy$), // Cleanup when the component is destroyed.
        )
        .subscribe()
    }
  }

  download(intake: IntakeModel, event: MouseEvent, file?: string) {
    const asHtml = event.altKey // if they hold the alt key, grab the PDF output as HTML (this is for debugging)
    this.progressService.start(PROGRESS_KEY.DOWNLOAD)
    this.restService
      .getIntakePdfLink(intake.id, {
        file,
        html: asHtml,
      })
      .pipe(takeUntil(this.onDestroy$))
      .subscribe({
        next: (resp) => {
          this.fileService.saveAs(resp.data)
        },
        error: () => {
          alert('Failed to download intake form, please try again')
        },
        complete: () => {
          this.progressService.stop(PROGRESS_KEY.DOWNLOAD)
        },
      })
  }

  downloadPDFDocument({ url, pdfFileName }: ISectionPDFDocumentValue) {
    this.fileService.saveAs({
      url,
      pdfFileName,
    })
  }

  downloadImage(image: CustomQuestionSchema) {
    this.fileService.saveAs({
      url: image.value,
      pdfFileName: `image.${image.extension}`,
    })
  }
}
